import { useState, useMemo } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LoadingView } from "@/components";
import { useAccountData } from "@trader-dashboard/shared";
import { usePairAnalysis } from "@/context/PairAnalysisContext";
import {
  DEFAULT_SCENARIO_PIPS,
  buildDrawdownSummary,
  buildPairExposures,
  buildScenarioCurve,
} from "@trader-dashboard/shared";
import { DrawdownSummaryCards, UnpricedPairsWarning } from "./DrawdownSummaryCards";
import { DrawdownScenarioChart } from "./DrawdownScenarioChart";
import { DrawdownPairTable } from "./DrawdownPairTable";

const REFERENCE_PIP_OPTIONS = [100, 200, 500];

export const DrawdownTab = (): React.ReactNode => {
  const { drawdown, drawdownLoading, drawdownError } = usePairAnalysis();
  const { accountData } = useAccountData();
  const [referencePips, setReferencePips] = useState(200);

  const equity = accountData?.equity;
  const positions = useMemo(() => drawdown?.openOrders ?? [], [drawdown]);

  const summary = useMemo(
    () => buildDrawdownSummary(positions, equity, referencePips),
    [positions, equity, referencePips]
  );
  const exposures = useMemo(
    () => buildPairExposures(positions, referencePips),
    [positions, referencePips]
  );
  const curve = useMemo(
    () => buildScenarioCurve(positions, equity, DEFAULT_SCENARIO_PIPS),
    [positions, equity]
  );

  if (drawdownLoading) return <LoadingView />;
  if (drawdownError) return <p className="text-red-500">Error: {drawdownError}</p>;

  if (positions.length === 0) {
    return (
      <Card>
        <CardContent className="flex h-[240px] items-center justify-center text-sm text-muted-foreground">
          No open positions — nothing at risk right now
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4">
      <DrawdownSummaryCards summary={summary} referencePips={referencePips} />
      <UnpricedPairsWarning pairs={summary.unpricedPairs} />

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>Estimated Drawdown</CardTitle>
              <CardDescription>
                Loss if every open position moved against you at once. Value per pip is derived
                from this account's own closed trades.
              </CardDescription>
            </div>
            <Tabs value={String(referencePips)} onValueChange={(v) => setReferencePips(Number(v))}>
              <TabsList className="h-8">
                {REFERENCE_PIP_OPTIONS.map((pips) => (
                  <TabsTrigger key={pips} value={String(pips)} className="px-3 py-1 text-xs">
                    {pips}p
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent className="grid gap-4">
          <DrawdownScenarioChart curve={curve} equity={equity} referencePips={referencePips} />
          <DrawdownPairTable exposures={exposures} referencePips={referencePips} />
          <p className="text-xs text-muted-foreground">
            An estimate, not a margin-call calculation: it assumes fully correlated adverse moves
            and excludes slippage, spread widening, and swap accrued while the move plays out.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
